import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';

import { DerivativesOpenedPositionProjection } from 'common/api/autogenerated/trading-gateway';

import { decimalAddition, localizeNumber } from 'common/helpers';
import { useI18n } from 'common/hooks';

import { getFeeSign, getPnlSign } from 'views/derivatives/orders/utils';

interface PositionFeeProps
  extends Pick<
    DerivativesOpenedPositionProjection,
    'tradingFee' | 'fundingFee' | 'liquidationPenalty' | 'markPriceUnrealizedPnL'
  > {
  currency?: string | null;
  digits: number;
  labels: {
    pnl?: string;
    total?: string;
  };
}

export const PositionPnlTooltip: React.FC<React.PropsWithChildren<PositionFeeProps>> = React.memo(
  ({ tradingFee = 0, fundingFee, liquidationPenalty = 0, markPriceUnrealizedPnL = 0, currency, digits, labels }) => {
    const { translate } = useI18n();

    const totalFee = useMemo(
      () => decimalAddition(tradingFee, fundingFee, liquidationPenalty).toNumber(),
      [fundingFee, liquidationPenalty, tradingFee],
    );

    const getClassNameForPnl = useCallback(
      (value = 0) =>
        classNames({
          red_color_text: value < 0,
          green_color_text: value >= 0,
        }),
      [],
    );

    const feeSign = useCallback(getFeeSign, []);
    const pnlSign = useCallback(getPnlSign, []);

    const tradingFeeFormatted = useMemo(() => {
      if (tradingFee === undefined) return undefined;

      const _formattedValue = localizeNumber(tradingFee, { maxDigits: digits });
      const _sign = feeSign(tradingFee, _formattedValue);
      return `${_sign}${_formattedValue} ${currency}`;
    }, [currency, digits, feeSign, tradingFee]);

    const fundingFeeFormatted = useMemo(() => {
      if (fundingFee === undefined) return undefined;

      const _formattedValue = localizeNumber(fundingFee, { maxDigits: digits });
      const _sign = feeSign(fundingFee, _formattedValue);
      return `${_sign}${_formattedValue} ${currency}`;
    }, [currency, digits, feeSign, fundingFee]);

    const liquidationPenaltyFeeFormatted = useMemo(() => {
      const _formattedValue = localizeNumber(liquidationPenalty, {
        maxDigits: digits,
      });
      const _sign = feeSign(liquidationPenalty, _formattedValue);
      return `${_sign}${_formattedValue} ${currency}`;
    }, [currency, digits, feeSign, liquidationPenalty]);

    const openPnlFormatted = useMemo(() => {
      const _formattedValue = localizeNumber(markPriceUnrealizedPnL, {
        maxDigits: digits,
      });
      const _sign = pnlSign(markPriceUnrealizedPnL, _formattedValue);
      return `${_sign}${_formattedValue} ${currency}`;
    }, [currency, digits, pnlSign, markPriceUnrealizedPnL]);

    const totalPnl = useMemo(
      () => decimalAddition(markPriceUnrealizedPnL, totalFee).toNumber(),
      [markPriceUnrealizedPnL, totalFee],
    );

    const totalPnlFormatted = useMemo(() => {
      const _formattedValue = localizeNumber(totalPnl, { maxDigits: digits });
      const _sign = pnlSign(totalPnl, _formattedValue);
      return `${_sign}${_formattedValue} ${currency}`;
    }, [totalPnl, digits, pnlSign, currency]);

    const totalPnlClassNames = useMemo(() => getClassNameForPnl(totalPnl), [getClassNameForPnl, totalPnl]);

    const tooltip = useMemo(
      () => (
        <div className='flex flex-column gap-4 Body2_reg_14'>
          <p className='flex gap-12 flex-justify-space-between'>
            <span>{labels.pnl}</span>
            <span>{openPnlFormatted}</span>
          </p>

          {!!tradingFeeFormatted && (
            <p className='flex gap-12 flex-justify-space-between'>
              <span>{translate.toText('mobile:derivative:position:details:fee:tooltip:trading')}</span>
              <span>{tradingFeeFormatted}</span>
            </p>
          )}

          {!!fundingFeeFormatted && (
            <p className='flex gap-12 flex-justify-space-between'>
              <span>{translate.toText('mobile:derivative:position:details:fee:tooltip:funding')}</span>
              <span>{fundingFeeFormatted}</span>
            </p>
          )}

          {!!liquidationPenalty && (
            <p className='flex gap-12 flex-justify-space-between'>
              <span>{translate.toText('mobile:derivative:position:details:fee:tooltip:penalty')}</span>
              <span>{liquidationPenaltyFeeFormatted}</span>
            </p>
          )}

          <span className='tooltip-divider' />

          <p className='flex gap-12 flex-justify-space-between'>
            <span>{labels.total}</span>
            <span className={totalPnlClassNames}>{totalPnlFormatted}</span>
          </p>
        </div>
      ),
      [
        fundingFeeFormatted,
        labels.pnl,
        labels.total,
        liquidationPenalty,
        liquidationPenaltyFeeFormatted,
        openPnlFormatted,
        totalPnlClassNames,
        totalPnlFormatted,
        tradingFeeFormatted,
        translate,
      ],
    );

    return tooltip;
  },
);
